import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Subscription, SubscriptionSummary, SubscriptionCalendarDay } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

interface SubscriptionsState {
  subscriptions: Subscription[];
  selectedSubscription: Subscription | null;
  summary: SubscriptionSummary | null;
  calendar: SubscriptionCalendarDay[];
  calendarLoading: boolean;
  calendarTotalMonthly: number;
  isLoading: boolean;
  isSummaryLoading: boolean;
  error: string | null;
}

const initialState: SubscriptionsState = {
  subscriptions: [],
  selectedSubscription: null,
  summary: null,
  calendar: [],
  calendarLoading: false,
  calendarTotalMonthly: 0,
  isLoading: false,
  isSummaryLoading: false,
  error: null,
};

export const fetchSubscriptions = createAsyncThunk(
  'subscriptions/fetchSubscriptions',
  async (params: { status?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.subscriptions.list, { params });
      return (response.data.data || response.data) as Subscription[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch subscriptions'));
    }
  }
);

export const createSubscription = createAsyncThunk(
  'subscriptions/createSubscription',
  async (data: Partial<Subscription>, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.subscriptions.create, data);
      return (response.data.data || response.data) as Subscription;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create subscription'));
    }
  }
);

export const updateSubscription = createAsyncThunk(
  'subscriptions/updateSubscription',
  async ({ id, data }: { id: string; data: Partial<Subscription> }, { rejectWithValue }) => {
    try {
      const response = await api.put(endpoints.subscriptions.update(id), data);
      return (response.data.data || response.data) as Subscription;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update subscription'));
    }
  }
);

export const deleteSubscription = createAsyncThunk(
  'subscriptions/deleteSubscription',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.subscriptions.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete subscription'));
    }
  }
);

export const pauseSubscription = createAsyncThunk(
  'subscriptions/pauseSubscription',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.patch(endpoints.subscriptions.pause(id));
      return (response.data.data || response.data) as Subscription;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to pause subscription'));
    }
  }
);

export const resumeSubscription = createAsyncThunk(
  'subscriptions/resumeSubscription',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.patch(endpoints.subscriptions.resume(id));
      const fullBody = response.data;
      const subscription = (fullBody.data ?? fullBody) as Subscription;
      const missedCycles = (fullBody.missedCycles ?? []) as Array<{ date: string; amount: number; currency: string }>;
      const truncated = (fullBody.truncated ?? false) as boolean;
      return { subscription, missedCycles, truncated };
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to resume subscription'));
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscriptions/cancelSubscription',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.patch(endpoints.subscriptions.cancel(id));
      const apiResponse = response.data.data || response.data;
      const subscription = (apiResponse.data ?? apiResponse) as Subscription;
      const pendingTransactionCount = (apiResponse.pendingTransactionCount ?? 0) as number;
      return { subscription, pendingTransactionCount };
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to cancel subscription'));
    }
  }
);

export const confirmPayment = createAsyncThunk(
  'subscriptions/confirmPayment',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.patch(endpoints.subscriptions.confirmPayment(id));
      return (response.data.data || response.data) as Subscription;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to confirm payment'));
    }
  }
);

export const previewTransaction = createAsyncThunk(
  'subscriptions/previewTransaction',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.subscriptions.previewTransaction(id));
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to preview transaction'));
    }
  }
);

export const fetchSubscriptionSummary = createAsyncThunk(
  'subscriptions/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.subscriptions.summary);
      return (response.data.data || response.data) as SubscriptionSummary;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch subscription summary'));
    }
  }
);

export const fetchSubscriptionCalendar = createAsyncThunk(
  'subscriptions/fetchCalendar',
  async (params: { month?: string } | undefined, { rejectWithValue }) => {
    try {
      const url = params?.month
        ? `${endpoints.subscriptions.calendar}?month=${params.month}`
        : endpoints.subscriptions.calendar;
      const response = await api.get(url);
      const body = response.data.data || response.data;
      return {
        billingDates: (body.billingDates || []) as SubscriptionCalendarDay[],
        totalMonthly: (body.totalMonthly || 0) as number,
      };
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch subscription calendar'));
    }
  }
);

const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    clearSubscriptionError: (state) => { state.error = null; },
    setSelectedSubscription: (state, action: PayloadAction<Subscription | null>) => {
      state.selectedSubscription = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch
    builder.addCase(fetchSubscriptions.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchSubscriptions.fulfilled, (state, action) => { state.isLoading = false; state.subscriptions = action.payload; });
    builder.addCase(fetchSubscriptions.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Create
    builder.addCase(createSubscription.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(createSubscription.fulfilled, (state, action) => { state.isLoading = false; state.subscriptions.push(action.payload); });
    builder.addCase(createSubscription.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Update
    builder.addCase(updateSubscription.fulfilled, (state, action) => {
      const idx = state.subscriptions.findIndex(s => s._id === action.payload._id);
      if (idx !== -1) state.subscriptions[idx] = action.payload;
    });
    builder.addCase(updateSubscription.rejected, (state, action) => { state.error = action.payload as string; });

    // Delete
    builder.addCase(deleteSubscription.fulfilled, (state, action) => {
      state.subscriptions = state.subscriptions.filter(s => s._id !== action.payload);
    });
    builder.addCase(deleteSubscription.rejected, (state, action) => { state.error = action.payload as string; });

    // Pause
    builder.addCase(pauseSubscription.fulfilled, (state, action) => {
      const idx = state.subscriptions.findIndex(s => s._id === action.payload._id);
      if (idx !== -1) state.subscriptions[idx] = action.payload;
    });

    // Resume
    builder.addCase(resumeSubscription.fulfilled, (state, action) => {
      const idx = state.subscriptions.findIndex(s => s._id === action.payload.subscription._id);
      if (idx !== -1) state.subscriptions[idx] = action.payload.subscription;
    });

    // Cancel
    builder.addCase(cancelSubscription.fulfilled, (state, action) => {
      const idx = state.subscriptions.findIndex(s => s._id === action.payload.subscription._id);
      if (idx !== -1) state.subscriptions[idx] = action.payload.subscription;
    });

    // Confirm Payment
    builder.addCase(confirmPayment.fulfilled, (state, action) => {
      const idx = state.subscriptions.findIndex(s => s._id === action.payload._id);
      if (idx !== -1) state.subscriptions[idx] = action.payload;
    });

    // Summary
    builder.addCase(fetchSubscriptionSummary.pending, (state) => { state.isSummaryLoading = true; });
    builder.addCase(fetchSubscriptionSummary.fulfilled, (state, action) => { state.isSummaryLoading = false; state.summary = action.payload; });
    builder.addCase(fetchSubscriptionSummary.rejected, (state) => { state.isSummaryLoading = false; });

    // Calendar
    builder.addCase(fetchSubscriptionCalendar.pending, (state) => { state.calendarLoading = true; });
    builder.addCase(fetchSubscriptionCalendar.fulfilled, (state, action) => {
      state.calendarLoading = false;
      state.calendar = action.payload.billingDates;
      state.calendarTotalMonthly = action.payload.totalMonthly;
    });
    builder.addCase(fetchSubscriptionCalendar.rejected, (state) => { state.calendarLoading = false; });
  },
});

export const { clearSubscriptionError, setSelectedSubscription } = subscriptionsSlice.actions;
export default subscriptionsSlice.reducer;
